<script lang="ts">
    import { afterUpdate, onDestroy, onMount } from "svelte";

    import type { Histogram } from "../bar_graph";
    import { tweened } from "svelte/motion";
    import * as easings from "svelte/easing";
    import SvelteTooltip from "svelte-tooltip";

    export let histogram: Histogram;
    export let onChange: ((value: number) => void) | null = null;
    export let format: (value: number) => string;
    export const formatTooltip: ((count: number) => string) | null = null;
    export let value = 0;
    export let showBars = true;
    let sliderPos: number = 0.5;
    let sliderElement: HTMLDivElement;
    let thumbElement: HTMLDivElement;
    let dragging = false;
    export let selectableIndexRange: [number | null, number | null] = [null, null];
    const thumbWidth = 20;
    const barHeight = 100;

    const computeBarHeights = (bucketSizes: number[]) => {
        const barHeights: number[] = [];
        const maxBucketCount = Math.max(1, ...histogram.bucketSizes);
        for (const bucketSize of bucketSizes) {
            barHeights.push(bucketSize / maxBucketCount);
        }
        return barHeights;
    };

    const barHeights = tweened(computeBarHeights(histogram.bucketSizes), { duration: 300, easing: easings.cubicOut });

    $: {
        barHeights.set(computeBarHeights(histogram.bucketSizes));
    }

    const snap = (v: number) => {
        let idx = Math.round(v * histogram.bucketSizes.length);
        idx = Math.max(
            selectableIndexRange[0] || 0,
            Math.min(selectableIndexRange[1] || histogram.bucketSizes.length, idx)
        );
        return idx;
    };

    const updateSliderPosition = (clientX: number) => {
        const rect = sliderElement.getBoundingClientRect();
        let slider = (clientX - rect.left) / rect.width;
        slider = Math.max(0.0, Math.min(1.0, slider));
        const index = snap(slider);
        value = histogram.bucketSplits[index];
    };

    const mouseDown = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        dragging = true;
        window.addEventListener("mousemove", mouseMove);
        window.addEventListener("mouseup", mouseUp);
    };

    const touchStart = (e: TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        dragging = true;
        window.addEventListener("touchmove", touchMove);
        window.addEventListener("touchend", touchEnd);
    };

    const mouseMove = (e: MouseEvent) => {
        updateSliderPosition(e.clientX);
        if ((e.buttons & 1) == 0) {
            // Missed mouse up event.
            // This can happen if the browser lost focus while the user was dragging the slider.
            mouseUp(e);
        }
    };

    const touchMove = (e: TouchEvent) => {
        updateSliderPosition(e.touches[0].clientX);
    };

    $: {
        console.assert(
            histogram.bucketSizes.length + 1 == histogram.bucketSplits.length,
            "Number of splits in histogram should be exactly one more than the number of buckets"
        );
        let buckets = histogram.bucketSizes.length;
        let idx = histogram.bucketSplits.findIndex((x) => x >= value);
        if (idx == -1 || idx == histogram.bucketSplits.length - 1) idx = histogram.bucketSplits.length - 2;
        const interp =
            (value - histogram.bucketSplits[idx]) / (histogram.bucketSplits[idx + 1] - histogram.bucketSplits[idx]);
        sliderPos = (idx + interp) / buckets;
    }

    const onResize = () => {
        // The style depends on the element size, so force the html to update
        sliderPos = sliderPos;
    };

    onMount(() => {
        window.addEventListener("resize", onResize);
    });

    onDestroy(() => {
        window.removeEventListener("resize", onResize);
    });

    const mouseUp = (e: MouseEvent) => {
        updateSliderPosition(e.clientX);
        dragging = false;
        window.removeEventListener("mousemove", mouseMove);
        window.removeEventListener("mouseup", mouseUp);
        if (onChange != null) onChange(value);
    };

    const touchEnd = (e: TouchEvent) => {
        dragging = false;
        window.removeEventListener("touchmove", touchMove);
        window.removeEventListener("touchend", touchEnd);
        if (onChange != null) onChange(value);
    };
</script>

<div class="histogram-slider">
    {#if showBars}
        <div class="bars">
            {#each $barHeights as _, i}
                <div
                    style={`height: ${barHeight * $barHeights[i]}px`}
                    class:left-of-slider={(histogram.bucketSplits[i] + histogram.bucketSplits[i + 1]) * 0.5 < value}
                />
            {/each}
        </div>
    {/if}
    <div class="slider">
        <div class="slider-range" bind:this={sliderElement} on:mousedown={mouseDown} on:touchstart={touchStart}>
            <div
                class="slider-range-left"
                style={`width: ${sliderElement !== undefined ? sliderPos * sliderElement.clientWidth : 0}px;`}
            />
        </div>
        <div
            class="thumb"
            class:active={dragging}
            style={`left: ${
                sliderElement !== undefined ? sliderPos * sliderElement.clientWidth - 0.5 * thumbWidth : 0
            }px;`}
            bind:this={thumbElement}
            on:mousedown={mouseDown}
            on:touchstart={touchStart}
        >
            <div class="value-anchor">
                <div class="value">
                    {format(value)}
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    .histogram-slider {
        display: flex;
        flex-direction: column;
        justify-content: start;
        align-items: stretch;
        padding-bottom: 40px;
        --thumb-width: 16px;
        --thumb-height: 16px;
        --slider-height: 2px;
        gap: 0px;
        margin-top: var(--thumb-height);
        /* gap: calc(var(--thumb-height)*0.5 + 4px); */
    }

    .slider-range {
        display: flex;
        flex-direction: row;
        justify-content: start;
    }

    .slider-range .slider-range-left {
        background: var(--blue);
        height: 2px;
    }

    .bars {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: end;
        gap: 2px;
        height: 100px;
    }

    .bars div {
        background: var(--blue);
        flex: 1;
    }

    .bars div:not(.left-of-slider) {
        background: var(--blue-transparent);
    }

    .slider {
        background: var(--blue-transparent);
        height: var(--slider-height);
    }

    .thumb {
        position: relative;
        width: var(--thumb-width);
        height: var(--thumb-height);
        top: calc(0px - (var(--thumb-height) + var(--slider-height)) * 0.5);
        background: var(--blue);
        border-radius: 100px;
        border: 2px solid white;
        cursor: grab;
        user-select: none;
    }

    .thumb.active {
        box-shadow: 0px 0px 0px 4px rgba(0, 0, 0, 0.2);
    }

    .value-anchor {
        position: relative;
        top: calc(var(--thumb-height) + 4px);
        left: calc(0px - (100px - var(--thumb-width)) * 0.5);
        width: 100px;
        display: flex;
        flex-direction: row;
        justify-content: center;
    }

    .value {
        display: flex;
        flex-direction: row;
        justify-content: center;
        background: var(--blue);
        color: white;
        padding: 3px 4px;
        border-radius: 4px;
    }
</style>
